
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

// Animates an integer from its previous value to the new one over ~0.5s.
// Honors prefers-reduced-motion by rendering the exact value instantly (no
// setState in the effect body on that path — avoids the cascading-render lint).
export default function CountUp({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const [animated, setAnimated] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduce) return; // reduced-motion path renders `value` directly below
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const dur = 500;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setAnimated(Math.round(from + (to - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, reduce]);

  const shown = reduce ? value : animated;
  return <span className={className}>{shown.toLocaleString()}</span>;
}
